import { useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
    ArrowLeft,
    BadgeCheck,
    Sparkles,
    Eye,
    MapPin,
    Briefcase,
    CalendarDays,
    UserPlus,
    UserMinus,
    MessageCircle,
    Star,
    Flag,
    ShieldCheck,
} from "lucide-react";
import { getUsers } from "@/lib/storage";
import { listServicesByOwner, listEventsByOwner } from "@/lib/entitiesStore";
import {
    isFollowing,
    getFollowing,
    getFollowers,
    toggleFollow,
    areConnected,
    getOrCreateConversation,
    addRequest,
} from "@/lib/socialStore";
import {
    getReviewsForSeller,
    getSellerRatingSummary,
} from "@/lib/bookingStore";
import { ServiceCard } from "@/components/ServiceCard";
import { EventCard } from "@/components/EventCard";
import InitialAvatar from "@/components/InitialAvatar";
import ReportSheet from "@/components/ReportSheet";
import MoreMenu from "@/components/MoreMenu";
import { useAuth } from "@/context/AuthContext";
import { useSocialVersion } from "@/lib/hooks";
import { MOCK_PEOPLE } from "@/lib/mockData";

const TABS = [
    { id: "services", label: "Services", icon: Briefcase },
    { id: "events",   label: "Events",   icon: CalendarDays },
    { id: "portfolio", label: "Portfolio", icon: Sparkles },
    { id: "reviews",  label: "Reviews",  icon: Star },
];

const PublicProfile = () => {
    const navigate = useNavigate();
    const { username } = useParams();
    const [params] = useSearchParams();
    const isPreview = params.get("preview") === "1";
    const { user: me } = useAuth();
    const v = useSocialVersion();
    const [tab, setTab] = useState("services");
    const [reportSent, setReportSent] = useState(false);
    const [reportOpen, setReportOpen] = useState(false);

    // Resolve target — first try real users, then MOCK_PEOPLE so /u/<mock_username> works.
    const target = useMemo(() => {
        const u = String(username || "").toLowerCase();
        const real = getUsers().find((x) => x.username?.toLowerCase() === u);
        if (real) return real;
        const mock = MOCK_PEOPLE.find((p) => p.username?.toLowerCase() === u);
        if (mock) {
            return {
                id: mock.id,
                fullName: mock.name,
                username: mock.username,
                bio: mock.role,
                location: mock.location,
                premium: false,
                verified: false,
                avatar: null,
                _isMock: true,
            };
        }
        return null;
    }, [username]);

    const isSelf = !!(me && target && me.id === target.id);
    const following = useMemo(
        () => (me && target && !isSelf ? isFollowing(me.id, target.id) : false),
        [me, target, isSelf, v]
    );
    const connected = useMemo(
        () => (me && target && !isSelf ? areConnected(me.id, target.id) : false),
        [me, target, isSelf, v]
    );
    const followersCount = useMemo(
        () => (target ? getFollowers(target.id).length : 0),
        [target, v]
    );
    const followingCount = useMemo(
        () => (target ? getFollowing(target.id).length : 0),
        [target, v]
    );

    if (!target) {
        return (
            <div className="screen items-center justify-center px-6" data-testid="public-profile-not-found">
                <p className="text-[14px] text-muted-foreground">User @{username} not found.</p>
                <button onClick={() => navigate(-1)} className="mt-3 text-primary text-[13px] font-medium">
                    Go back
                </button>
            </div>
        );
    }

    const services = listServicesByOwner(target.id);
    const events = listEventsByOwner(target.id);
    const reviews = getReviewsForSeller(target.id);
    const ratingSummary = getSellerRatingSummary(target.id);
    const isBusiness = target.accountType === "business";

    const handleFollow = () => {
        if (!me || isSelf) return;
        toggleFollow(me.id, target.id);
    };

    const handleMessage = () => {
        if (!me || isSelf) return;
        const peer = { id: target.id, name: target.fullName, username: target.username };
        if (connected) {
            const convId = getOrCreateConversation(me.id, peer);
            navigate(`/messages/${convId}`);
        } else {
            // Send as request
            addRequest(me.id, peer, "Wants to connect");
            navigate("/messages?tab=requests");
        }
    };

    const handleReport = () => {
        setReportOpen(true);
    };

    return (
        <div className="min-h-full pb-10 animate-fade-in" data-testid="public-profile-screen">
            <header
                className="px-4 pt-[max(14px,env(safe-area-inset-top))] pb-3 flex items-center gap-2"
                style={{ background: "linear-gradient(180deg, hsl(var(--primary) / 0.08) 0%, transparent 100%)" }}
            >
                <button
                    onClick={() => navigate(-1)}
                    data-testid="public-profile-back"
                    className="h-10 w-10 flex items-center justify-center rounded-xl hover:bg-muted active:scale-95"
                    aria-label="Back"
                >
                    <ArrowLeft size={20} />
                </button>
                <h1 className="font-display text-[16px] font-semibold flex-1 truncate">
                    @{target.username}
                </h1>
                {!isSelf && (
                    <MoreMenu
                        target={{ kind: "user", id: target.id, name: target.fullName, username: target.username }}
                        testId="public-profile-more"
                        position="right"
                        className="h-10 w-10 rounded-xl bg-transparent border-0 hover:bg-muted"
                    />
                )}
            </header>

            {isPreview && isSelf && (
                <div
                    data-testid="public-profile-preview-banner"
                    className="mx-4 mb-3 px-3 py-2 rounded-xl bg-primary/10 border border-primary/25 text-primary text-[12px] font-medium inline-flex items-center gap-2"
                >
                    <Eye size={13} />
                    Preview mode — this is what others see
                </div>
            )}

            {isBusiness && target.banner && (
                <div
                    className="relative h-32 w-full overflow-hidden"
                    data-testid="public-profile-banner"
                >
                    <img src={target.banner} alt="" className="absolute inset-0 h-full w-full object-cover" />
                    <div
                        aria-hidden
                        className="absolute inset-0"
                        style={{ background: "linear-gradient(180deg, rgba(0,0,0,0) 40%, hsl(var(--background)) 100%)" }}
                    />
                </div>
            )}

            <section className="px-5 pt-2">
                <div className="flex items-center gap-4">
                    {target.avatar ? (
                        <img
                            src={target.avatar}
                            alt={target.fullName}
                            className={[
                                "h-20 w-20 rounded-full object-cover",
                                target.premium
                                    ? "ring-2 ring-primary shadow-[0_0_0_4px_hsl(var(--primary)/0.15)]"
                                    : "ring-2 ring-border",
                            ].join(" ")}
                            data-testid="public-profile-avatar"
                        />
                    ) : (
                        <div data-testid="public-profile-avatar">
                            <InitialAvatar name={target.fullName || target.username} size={80} />
                        </div>
                    )}
                    <div className="min-w-0 flex-1">
                        <h2 className="font-display text-[18px] font-semibold tracking-tight truncate flex items-center gap-1.5">
                            {target.fullName || target.username}
                            {target.verified && (
                                <BadgeCheck size={16} className="text-primary shrink-0" data-testid="public-profile-verified" />
                            )}
                            {target.premium && (
                                <span
                                    className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-[10px] font-semibold text-primary-foreground"
                                    style={{ background: "linear-gradient(135deg, hsl(var(--primary)), hsl(var(--primary) / 0.78))" }}
                                    data-testid="public-profile-premium"
                                >
                                    <Sparkles size={10} />
                                    Premium
                                </span>
                            )}
                        </h2>
                        <p className="text-[13px] text-muted-foreground truncate">@{target.username}</p>
                        {target.location && (
                            <p className="mt-0.5 inline-flex items-center gap-1 text-[12px] text-muted-foreground">
                                <MapPin size={11} />
                                {target.location}
                            </p>
                        )}
                    </div>
                </div>
                {target.bio && (
                    <p className="mt-3 text-[13.5px] leading-relaxed text-foreground/85" data-testid="public-profile-bio">
                        {target.bio}
                    </p>
                )}

                {/* Followers / Following — clickable */}
                <div className="mt-3 grid grid-cols-2 gap-2" data-testid="public-profile-stats">
                    <button
                        type="button"
                        data-testid="public-profile-followers"
                        onClick={() => navigate(`/u/${target.username}/followers`)}
                        className="rounded-2xl border border-border bg-card hover:bg-accent/40 active:scale-[0.97] transition-[transform,background-color] py-2.5"
                    >
                        <div className="font-display text-[16px] font-semibold tracking-tight">{followersCount}</div>
                        <div className="text-[11px] text-muted-foreground uppercase tracking-wider mt-0.5">Followers</div>
                    </button>
                    <button
                        type="button"
                        data-testid="public-profile-following"
                        onClick={() => navigate(`/u/${target.username}/following`)}
                        className="rounded-2xl border border-border bg-card hover:bg-accent/40 active:scale-[0.97] transition-[transform,background-color] py-2.5"
                    >
                        <div className="font-display text-[16px] font-semibold tracking-tight">{followingCount}</div>
                        <div className="text-[11px] text-muted-foreground uppercase tracking-wider mt-0.5">Following</div>
                    </button>
                </div>

                {/* Business meta — category · starting price · rating */}
                {isBusiness && (
                    <div className="mt-3 flex items-center flex-wrap gap-2" data-testid="public-profile-business-meta">
                        {target.businessCategory && (
                            <span
                                data-testid="public-profile-business-category"
                                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-secondary text-[11.5px] font-medium border border-border"
                            >
                                <Briefcase size={11} />
                                {target.businessCategory}
                            </span>
                        )}
                        {Number(target.startingPrice) > 0 && (
                            <span
                                data-testid="public-profile-starting-price"
                                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-primary/10 text-primary text-[11.5px] font-semibold"
                            >
                                From ₦{Number(target.startingPrice).toLocaleString()}
                            </span>
                        )}
                        {ratingSummary.count > 0 && (
                            <span
                                data-testid="public-profile-rating-summary"
                                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-500/10 text-amber-700 dark:text-amber-400 text-[11.5px] font-semibold"
                            >
                                <Star size={11} className="fill-amber-400 text-amber-400" />
                                {ratingSummary.average} · {ratingSummary.count} {ratingSummary.count === 1 ? "review" : "reviews"}
                            </span>
                        )}
                    </div>
                )}

                {/* Book Now — business accounts with at least one service */}
                {isBusiness && !isSelf && services.length > 0 && (
                    <button
                        data-testid="public-profile-book-now"
                        onClick={() => navigate(`/services/${services[0].id}`)}
                        className="mt-4 w-full h-12 rounded-full text-primary-foreground inline-flex items-center justify-center gap-2 font-semibold text-[14px] active:scale-[0.98] transition-transform"
                        style={{
                            background: "linear-gradient(135deg, hsl(var(--primary)), hsl(var(--primary) / 0.78))",
                            boxShadow: "0 14px 32px -12px hsl(var(--primary) / 0.55)",
                        }}
                    >
                        <Briefcase size={15} />
                        Book Now
                    </button>
                )}

                {/* Action row — Follow / Message (hidden when viewing self & not preview) */}
                {!isSelf && (
                    <div className="mt-4 grid grid-cols-2 gap-2" data-testid="public-profile-actions">
                        <button
                            data-testid="public-profile-follow-btn"
                            onClick={handleFollow}
                            className={[
                                "h-11 rounded-full inline-flex items-center justify-center gap-1.5 font-medium text-[13px] active:scale-[0.98] transition-transform",
                                following
                                    ? "bg-secondary text-foreground border border-border hover:bg-muted"
                                    : "text-primary-foreground",
                            ].join(" ")}
                            style={
                                following
                                    ? undefined
                                    : {
                                          background: "linear-gradient(135deg, hsl(var(--primary)), hsl(var(--primary) / 0.78))",
                                          boxShadow: "0 10px 28px -10px hsl(var(--primary) / 0.55)",
                                      }
                            }
                        >
                            {following ? <UserMinus size={14} /> : <UserPlus size={14} />}
                            {following ? "Following" : "Follow"}
                        </button>
                        <button
                            data-testid="public-profile-message-btn"
                            onClick={handleMessage}
                            className="h-11 rounded-full bg-secondary text-foreground border border-border hover:bg-muted inline-flex items-center justify-center gap-1.5 font-medium text-[13px] active:scale-[0.98]"
                        >
                            <MessageCircle size={14} />
                            {connected ? "Message" : "Send request"}
                        </button>
                    </div>
                )}
                {!isSelf && !connected && (
                    <p className="mt-2 inline-flex items-center gap-1 text-[11.5px] text-muted-foreground">
                        <ShieldCheck size={11} className="text-primary" />
                        First message will be sent as a request.
                    </p>
                )}
            </section>

            {/* Tabs: Services / Events / Reviews */}
            <div className="px-4 pt-5 flex gap-2 overflow-x-auto no-scrollbar" data-testid="public-profile-tabs">
                {TABS.map((t) => {
                    const Icon = t.icon;
                    return (
                        <button
                            key={t.id}
                            data-testid={`public-profile-tab-${t.id}`}
                            onClick={() => setTab(t.id)}
                            className={[
                                "shrink-0 h-10 px-3.5 rounded-xl text-[12.5px] font-medium inline-flex items-center justify-center gap-1.5 transition-colors",
                                tab === t.id
                                    ? "bg-primary text-primary-foreground shadow-soft"
                                    : "bg-secondary text-secondary-foreground hover:bg-muted",
                            ].join(" ")}
                        >
                            <Icon size={14} strokeWidth={2.2} />
                            {t.label}
                        </button>
                    );
                })}
            </div>

            <div className="px-5 pt-4" data-testid={`public-profile-tab-content-${tab}`}>
                {tab === "services" && (
                    services.length === 0 ? (
                        <p className="text-[12.5px] text-muted-foreground py-6 text-center">No services listed yet.</p>
                    ) : (
                        <div className="grid grid-cols-2 gap-3">
                            {services.map((s) => <ServiceCard key={s.id} service={s} />)}
                        </div>
                    )
                )}
                {tab === "events" && (
                    events.length === 0 ? (
                        <p className="text-[12.5px] text-muted-foreground py-6 text-center">No events hosted yet.</p>
                    ) : (
                        <div className="grid grid-cols-1 gap-3">
                            {events.map((e) => <EventCard key={e.id} event={e} />)}
                        </div>
                    )
                )}
                {tab === "portfolio" && (
                    !target.portfolio || target.portfolio.length === 0 ? (
                        <div className="py-10 text-center" data-testid="public-profile-portfolio-empty">
                            <Sparkles size={22} className="mx-auto text-muted-foreground/60" />
                            <p className="mt-2 text-[13px] font-medium">No portfolio yet</p>
                            <p className="text-[11.5px] text-muted-foreground mt-0.5">
                                Past work from this business will appear here.
                            </p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-3 gap-2" data-testid="public-profile-portfolio">
                            {target.portfolio.map((p) => (
                                <div key={p.id} className="aspect-square rounded-xl overflow-hidden border border-border bg-muted">
                                    <img src={p.url} alt="" className="h-full w-full object-cover" />
                                </div>
                            ))}
                        </div>
                    )
                )}
                {tab === "reviews" && (
                    reviews.length === 0 ? (
                        <div className="py-10 text-center" data-testid="public-profile-reviews-empty">
                            <Star size={22} className="mx-auto text-muted-foreground/60" />
                            <p className="mt-2 text-[13px] font-medium">No reviews yet</p>
                            <p className="text-[11.5px] text-muted-foreground mt-0.5">
                                Reviews from completed bookings will appear here.
                            </p>
                        </div>
                    ) : (
                        <ul className="space-y-2" data-testid="public-profile-reviews-list">
                            {reviews.map((r) => (
                                <li
                                    key={r.id}
                                    data-testid={`public-profile-review-${r.id}`}
                                    className="p-3 rounded-2xl border border-border bg-card"
                                >
                                    <div className="flex items-center gap-2">
                                        <span className="flex items-center gap-0.5">
                                            {[1, 2, 3, 4, 5].map((n) => (
                                                <Star
                                                    key={n}
                                                    size={12}
                                                    className={n <= r.rating ? "text-amber-400 fill-amber-400" : "text-muted-foreground/30"}
                                                />
                                            ))}
                                        </span>
                                        <span className="text-[11.5px] text-muted-foreground truncate">
                                            {r.buyerName}{r.serviceName ? ` · ${r.serviceName}` : ""}
                                        </span>
                                    </div>
                                    {r.comment && (
                                        <p className="mt-1.5 text-[13px] text-foreground/85 leading-snug">
                                            {r.comment}
                                        </p>
                                    )}
                                </li>
                            ))}
                        </ul>
                    )
                )}
            </div>

            {reportSent && (
                <div
                    data-testid="public-profile-report-toast"
                    role="status"
                    className="absolute left-1/2 -translate-x-1/2 top-[calc(env(safe-area-inset-top)+12px)] z-50 px-4 py-2.5 rounded-full bg-card border border-emerald-500/30 shadow-card text-emerald-600 dark:text-emerald-400 inline-flex items-center gap-2 text-[13px] font-medium animate-fade-up"
                >
                    <ShieldCheck size={16} />
                    Report submitted — thanks for keeping Neyra safe
                </div>
            )}

            <ReportSheet
                open={reportOpen}
                onClose={() => setReportOpen(false)}
                target={{ kind: "user", id: target.id, label: target.username }}
                onSubmitted={() => {
                    setReportSent(true);
                    setTimeout(() => setReportSent(false), 2400);
                }}
            />
        </div>
    );
};

export default PublicProfile;
